import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@app/components/ui/Toast/toast'
import { DOCUMENTS_QUERY_KEY } from '../documents.constants'
import {
  UpdateDocumentPayload,
  deleteDocument,
  fetchDocuments,
  getDocument,
  getDocumentPermissions,
  getDocumentPreviewUrl,
  updateDocument,
  updateDocumentPermissions,
} from '../service/documents.service'
import { DocumentsListResponse, DocumentsQuery } from '../documents.types'
import { DocumentEditFormData } from '../documents-upload.types'

const DOCUMENT_PERMISSIONS_QUERY_KEY = 'evidence-document-permissions'
const DOCUMENT_PREVIEW_QUERY_KEY = 'evidence-document-preview-url'

// Server-side search/filter/sort/pagination — keyed on the full query
export const useFetchDocuments = (query: DocumentsQuery) => {
  return useQuery<DocumentsListResponse>({
    queryKey: [DOCUMENTS_QUERY_KEY, query],
    queryFn: () => fetchDocuments(query),
    placeholderData: keepPreviousData,
  })
}

export const useFetchDocument = (id: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: [DOCUMENTS_QUERY_KEY, 'detail', id],
    queryFn: () => getDocument(id as string),
    enabled: enabled && !!id,
    retry: false,
  })
}

export const useFetchDocumentPermissions = (id: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: [DOCUMENT_PERMISSIONS_QUERY_KEY, id],
    queryFn: () => getDocumentPermissions(id as string),
    enabled: enabled && !!id,
  })
}

export const useFetchDocumentPreviewUrl = (id: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: [DOCUMENT_PREVIEW_QUERY_KEY, id],
    queryFn: () => getDocumentPreviewUrl(id as string),
    enabled: enabled && !!id,
    retry: false,
  })
}

export const useUpdateDocument = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      form,
      applyRoleChanges,
    }: {
      id: string
      form: DocumentEditFormData
      // Only PATCH when the user actually touched visibility or roleIds — the correct
      // fallback if the permissions fetch ever fails or is skipped.
      applyRoleChanges: boolean
    }) => {
      const payload: UpdateDocumentPayload = {
        title: form.title,
        description_md: form.descriptionMd || null,
        // See the matching cast in useDocumentUpload.ts for why this is safe.
        category: form.category as UpdateDocumentPayload['category'],
        source: form.source || null,
        organisation: form.organisation || null,
        published_at: form.publishedAt || null,
        notes_md: form.notesMd || null,
        tags: form.tags,
        georeference_type: form.georeference.type,
        geometry: form.georeference.geometry,
        visibility: form.visibility,
      }
      // The backend requires a document to already be organisation-visible before role
      // assignments can be set. Await updateDocument first so the visibility change is
      // committed before the permissions PATCH fires. Do not parallelise.
      const updated = await updateDocument(id, payload)
      if (form.visibility === 'organisation' && applyRoleChanges) {
        await updateDocumentPermissions(id, { user_roles: form.roleIds })
      }
      return updated
    },
    onSuccess: () => {
      toast({ description: 'Document updated.', position: 'top-right', type: 'success' })
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] })
    },
    onError: () =>
      toast({ description: 'Failed to update document.', position: 'top-right', type: 'error' }),
  })
}

export const useDeleteDocument = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => {
      toast({ description: 'Document deleted.', position: 'top-right', type: 'success' })
      queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY] })
    },
    onError: () =>
      toast({ description: 'Failed to delete document.', position: 'top-right', type: 'error' }),
  })
}
